import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { formatMomentDay } from "@/shared/lib";
import { Avatar, Icon, Panel, StarRating } from "@/shared/ui";

import type { CommentItem } from "../model/types";

/**
 * Карточка отзыва (доработки §6.1).
 *
 * **Личность автора приходит настоящей — и это отличает блок от подиума.**
 * На подиуме ТОП-3 действует opt-in §5, и человек без согласия виден как
 * «Трейдер #1842». Здесь имя и фотография показываются всегда, потому что
 * отзыв — добровольный публичный акт: перед отправкой форма прямо
 * предупреждает, под чем человек подписывается. Клиент, как и на подиуме,
 * ничего не решает — он рисует то, что прислал сервер.
 *
 * Личность приходит готовой и у отзыва от лица персоны — строки, заведённой
 * суперадмином из админки. Карточка о ней не знает и знать не должна: сервер
 * присылает те же четыре поля, что и для живого автора. Поэтому ради персон
 * этот файл править не пришлось, и тест ленты закрепляет это отдельно.
 *
 * **Бейджа «Пользователь робота» из §6.1 здесь нет, и это не упущение.**
 * Оставить отзыв может только тот, кто запускал робота, поэтому такой бейдж
 * стоял бы у всех и не сообщал бы ничего. Утверждение переехало на уровень
 * блока — «Отзыв может оставить только тот, кто запускал робота», — где оно
 * сильнее как social proof, а строка различает авторов по тому, что у них
 * действительно разное: работает робот сейчас или уже остановлен.
 *
 * **Ответ поддержки — отдельный визуальный стиль (§6.1) и никакой личности
 * оператора (§5).** Подпись фиксированная, из i18n; ни имени, ни аватарки в
 * ответе просто нет.
 *
 * Текст рендерится как текст. Никакого `dangerouslySetInnerHTML`: бэкенд
 * хранит отзыв дословно, вместе с `<script>`, и экранирование — это ровно то,
 * что React делает по построению.
 *
 * **Длинный отзыв сворачивается.** Контракт разрешает 2000 символов, а это три
 * экрана на телефоне: развёрнутый целиком, один такой отзыв выдавливает из поля
 * зрения и соседние, и кнопку запуска. Ограничение стоит на шести строках —
 * столько занимает обычный отзыв, поэтому у большинства карточек кнопки
 * «Показать полностью» не появляется вовсе.
 */
/** Длина, после которой отзыв сворачивается: примерно шесть строк на 390px. */
const COLLAPSE_THRESHOLD = 280;

interface CommentCardProps {
  comment: CommentItem;
  /** Кнопки «Полезно», «Пожаловаться», «Изменить», «Удалить» — их владеет фича. */
  actions?: ReactNode;
}

export function CommentCard({ comment, actions }: CommentCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { author } = comment;

  return (
    <Panel as="article" radius="lg" className="flex flex-col gap-2.5 p-4">
      <header className="flex items-start gap-3">
        <Avatar src={author.avatar_url} initial={author.initial} size={36} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="truncate text-label font-semibold text-text-primary">
              {author.display_name}
            </span>
            <span className="text-micro font-medium text-text-tertiary">
              {author.owner_state === "active"
                ? t("robots.comments.ownerActive")
                : t("robots.comments.ownerPast")}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {comment.rating == null ? null : <StarRating value={comment.rating} />}
            <span className="text-micro font-normal text-text-tertiary">
              {formatMomentDay(comment.created_at)}
            </span>
            {comment.edited_at === null ? null : (
              <span className="text-micro font-normal text-text-tertiary">
                {t("robots.comments.edited")}
              </span>
            )}
          </div>
        </div>
      </header>

      {comment.status === "pending" || comment.is_test_data ? (
        <div className="flex flex-wrap gap-2">
          {comment.status === "pending" ? (
            <Chip tone="pending">{t("robots.comments.pending")}</Chip>
          ) : null}
          {/* §6.2: seed-отзывы допустимы только с явной пометкой. Чип рисуется
              по флагу с бэкенда; в тексте такой строки стоит ещё и префикс —
              он переживает скриншот, а чип нет. */}
          {comment.is_test_data ? <Chip tone="test">{t("robots.comments.testData")}</Chip> : null}
        </div>
      ) : null}

      <div className="flex flex-col items-start gap-1">
        <p
          className={[
            "whitespace-pre-line break-words text-body font-normal text-text-primary",
            expanded ? "" : "line-clamp-6",
          ].join(" ")}
        >
          {comment.text}
        </p>
        {/* Порог на символах, а не на измерении высоты: считать реальные строки
            значило бы читать layout после рендера и мигать кнопкой на каждом
            повороте экрана. */}
        {comment.text.length > COLLAPSE_THRESHOLD ? (
          <button
            type="button"
            data-haptic="tap"
            onClick={() => {
              setExpanded((value) => !value);
            }}
            className="press text-caption font-medium text-action-primary"
          >
            {expanded ? t("robots.comments.collapse") : t("robots.comments.expand")}
          </button>
        ) : null}
      </div>

      {actions}

      {comment.replies.length === 0 ? null : (
        <div className="flex flex-col gap-2 border-l-2 border-border-subtle pl-3">
          {comment.replies.map((reply) => (
            <div key={reply.id} className="flex flex-col gap-1">
              <span className="flex items-center gap-1.5 text-caption font-semibold text-action-primary">
                <Icon name="shieldCheck" size={14} />
                {t("robots.comments.supportName")}
              </span>
              <p className="whitespace-pre-line break-words text-caption font-normal text-text-secondary">
                {reply.text}
              </p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function Chip({ tone, children }: { tone: "pending" | "test"; children: ReactNode }) {
  return (
    <span
      className={[
        "rounded-full px-2 py-0.5 text-micro font-semibold",
        tone === "pending"
          ? "bg-background-high text-text-secondary"
          : "bg-status-loss/15 text-status-loss",
      ].join(" ")}
    >
      {children}
    </span>
  );
}
